import {createAsyncThunk, createSlice} from '@reduxjs/toolkit'
import {
    emailValidate,
    securitycode,
    resetpassword,
    verifySecuritycode,
    resendSecuritycode
} from 'services/forgotPassword.service'

// Define the initial state for the forgotPassword slice
export interface forgotPassword {
    emailisLoading: boolean
    error: any
    response: object
    emailValidation: object
    userEmail: any
    securitycodeisLoading: boolean
    securitycodeerror: any
    securitycoderesponse: object
    verifySecuritycodeisLoading: boolean
    verifySecuritycodeerror: any
    verifySecuritycoderesponse: object
    resetpasswordisLoading: boolean
    resetpassworderror: any
    resetpasswordresponse: object
    resendIsLoading: boolean
    resendError: any
    resendResponse: any
    forgotPasswordid: any
    forgotPassowrdEmail: any
}

const initialState: forgotPassword = {
    emailisLoading: false,
    error: null,
    response: {},
    emailValidation: {},
    userEmail: null,
    securitycodeisLoading: false,
    securitycodeerror: null,
    securitycoderesponse: {},
    resetpasswordisLoading: false,
    resetpassworderror: null,
    resetpasswordresponse: {},
    verifySecuritycodeisLoading: false,
    verifySecuritycodeerror: null,
    verifySecuritycoderesponse: {},
    resendIsLoading: false,
    resendError: null,
    resendResponse: null,
    forgotPasswordid: null,
    forgotPassowrdEmail: null
}

// Function to throw an error if the response data indicates an error
const throwError = (data) => {
    if (!data.successFlag || typeof data === 'string') {
        throw Error(String(data.errorList).match(/text=(.*?)(?=\])/)?.[1] ?? data.errorList[0].text)
    }
}

// Define async thunks for various API calls
export const fetchForgotPassword = createAsyncThunk('emailValidate', async (payload: any, {rejectWithValue}) => {
    try {
        const {data} = await emailValidate(payload)
        throwError(data)
        return data
    } catch (err: any) {
        if (err.status >= 400 && err.status < 500) {
            const msg: string =
                String(err?.response?.errors[0]?.field) + ' ' + String(err?.response?.errors[0]?.defaultMessage)
            return rejectWithValue(msg)
        } else if (err.status > 500) {
            const errorMsg = err.errorList[0].text
            return rejectWithValue(errorMsg)
        }
        return rejectWithValue(err.message)
    }
})
export const fetchForgotPasswords = createAsyncThunk('securitycode', async (payload: any, {rejectWithValue}) => {
    try {
        const {data} = await securitycode(payload)
        throwError(data)
        return data.response
    } catch (err: any) {
        if (err.status >= 400 && err.status < 500) {
            const msg: string =
                String(err?.response?.errors[0]?.field) + ' ' + String(err?.response?.errors[0]?.defaultMessage)
            return rejectWithValue(msg)
        } else if (err.status > 500) {
            const errorMsg = err.errorList[0].text
            return rejectWithValue(errorMsg)
        }
        return rejectWithValue(err.message)
    }
})
export const fetchResendSecurityCode = createAsyncThunk('resendCode', async (payload: any, {rejectWithValue}) => {
    try {
        const {data} = await resendSecuritycode(payload)
        throwError(data)
        return data.response
    } catch (err: any) {
        if (err.status >= 400 && err.status < 500) {
            const msg: string =
                String(err?.response?.errors[0]?.field) + ' ' + String(err?.response?.errors[0]?.defaultMessage)
            return rejectWithValue(msg)
        } else if (err.status > 500) {
            const errorMsg = err.errorList[0].text
            return rejectWithValue(errorMsg)
        }
        return rejectWithValue(err.message)
    }
})
export const fetchVerifySecurityCode = createAsyncThunk('verifycode', async (payload: any, {rejectWithValue}) => {
    try {
        const {data} = await verifySecuritycode(payload)
        throwError(data)
        return 'matched'
    } catch (err: any) {
        if (err.status >= 400 && err.status < 500) {
            const msg: string =
                String(err?.response?.errors[0]?.field) + ' ' + String(err?.response?.errors[0]?.defaultMessage)
            return rejectWithValue(msg)
        } else if (err.status > 500) {
            const errorMsg = err.errorList[0].text
            return rejectWithValue(errorMsg)
        }
        return rejectWithValue(err.message)
    }
})
export const fetchresetcodePasswords = createAsyncThunk('resetpassword', async (payload: any, {rejectWithValue}) => {
    try {
        const {data} = await resetpassword(payload)

        throwError(data)

        return data.response
    } catch (err: any) {
        if (err.status >= 400 && err.status < 500) {
            const msg: string =
                String(err?.response?.errors[0]?.field) + ' ' + String(err?.response?.errors[0]?.defaultMessage)
            return rejectWithValue(msg)
        } else if (err.status > 500) {
            const errorMsg = err.errorList[0].text
            return rejectWithValue(errorMsg)
        }
        return rejectWithValue(err.message)
    }
})

// Create a Redux slice for the forgotPassword state
const forgotPasswordSlice = createSlice({
    name: 'forgotPassword',
    initialState,
    reducers: {
        setRestPasswordid: (state, action) => {
            state.forgotPasswordid = action.payload
        },
        setRestPasswordEmail: (state, action) => {
            state.forgotPassowrdEmail = action.payload
        }
    },
    extraReducers: function (builder) {
        builder.addCase(fetchForgotPassword.pending, (state: any, action) => {
            state.emailisLoading = true
            state.response = null
            state.userEmail = null
            state.error = null
        })
        builder.addCase(fetchForgotPassword.fulfilled, (state: any, action) => {
            state.emailisLoading = false
            state.emailValidation = action.payload
            state.error = null
            state.userEmail = action.meta.arg.email
        })
        builder.addCase(fetchForgotPassword.rejected, (state: any, action: any) => {
            state.emailisLoading = false
            state.emailValidation = null
            state.error = action.payload
        })

        builder.addCase(fetchForgotPasswords.pending, (state: any, action) => {
            state.securitycodeisLoading = true
        })
        builder.addCase(fetchForgotPasswords.fulfilled, (state: any, action) => {
            state.securitycodeisLoading = false
            state.securitycoderesponse = action.payload
            state.securitycodeerror = null
        })
        builder.addCase(fetchForgotPasswords.rejected, (state: any, action: any) => {
            state.securitycodeisLoading = false
            state.securitycoderesponse = null
            state.securitycodeerror = action.payload
        })
        builder.addCase(fetchVerifySecurityCode.pending, (state: any, action) => {
            state.verifySecuritycodeisLoading = true
            state.verifySecuritycoderesponse = null
            state.verifySecuritycodeerror = null
            // state.userEnteredCode = null
        })
        builder.addCase(fetchVerifySecurityCode.fulfilled, (state: any, action) => {
            state.verifySecuritycodeisLoading = false
            state.verifySecuritycoderesponse = action.payload
            state.verifySecuritycodeerror = null
            // state.userEnteredCode = action.meta.arg.email
        })
        builder.addCase(fetchVerifySecurityCode.rejected, (state: any, action: any) => {
            state.verifySecuritycodeisLoading = false
            state.verifySecuritycoderesponse = null
            state.verifySecuritycodeerror = action.payload
        })
        builder.addCase(fetchresetcodePasswords.pending, (state: any, action) => {
            state.resetpasswordisLoading = true
            state.resetpasswordresponse = null
            state.resetpassworderror = null
        })
        builder.addCase(fetchresetcodePasswords.fulfilled, (state: any, action) => {
            state.resetpasswordisLoading = false
            state.resetpasswordresponse = action.payload
            state.resetpassworderror = null
        })
        builder.addCase(fetchresetcodePasswords.rejected, (state: any, action: any) => {
            state.resetpasswordisLoading = false
            state.resetpasswordresponse = null
            state.resetpassworderror = action.payload
        })
        builder.addCase(fetchResendSecurityCode.pending, (state: any, action) => {
            state.resendIsLoading = true
            state.resendResponse = null
            state.resendError = null
        })
        builder.addCase(fetchResendSecurityCode.fulfilled, (state: any, action) => {
            state.resendIsLoading = false
            state.resendResponse = action.payload
            state.resendError = null
        })
        builder.addCase(fetchResendSecurityCode.rejected, (state: any, action: any) => {
            state.resendIsLoading = false
            state.resendResponse = null
            state.resendError = action.payload
        })
    }
})

// Export the reducer from the slice
export const {setRestPasswordid, setRestPasswordEmail} = forgotPasswordSlice.actions
export const {reducer: forgotPasswordReducer} = forgotPasswordSlice
